import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import SiteFooter from "../components/SiteFooter";

export default function Home() {
  const [settings, setSettings] = useState(null);
  const [carousel, setCarousel] = useState([]);
  const [events, setEvents] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeSlide, setActiveSlide] = useState(0);
  const [eventIndex, setEventIndex] = useState(0);
  const [aboutUs, setAboutUs] = useState(null);

  // Feedback form (kept but UI will be commented)
  const [fbForm, setFbForm] = useState({
    name: "",
    email: "",
    mobile: "",
    message: "",
  });
  const [fbStatus, setFbStatus] = useState("");
  const [fbSubmitting, setFbSubmitting] = useState(false);

  // ---- Browser push notification state (bell) ----
  const [pushSupported, setPushSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [bellLoading, setBellLoading] = useState(false);

  // Generate / read browser id from localStorage
  const getBrowserId = () => {
    try {
      const key = "yogapay_browser_id";
      let id = window.localStorage.getItem(key);
      if (!id) {
        id = Math.random().toString(36).slice(2) + Date.now().toString(36);
        window.localStorage.setItem(key, id);
      }
      return id;
    } catch {
      return "unknown";
    }
  };

  // On mount, check support + sync subscription from OneSignal (fallback to localStorage)
  useEffect(() => {
    const supported =
      "Notification" in window &&
      "serviceWorker" in navigator &&
      "PushManager" in window;

    setPushSupported(supported);

    if (!supported) return;

    // 1) Try to sync from OneSignal real state (best)
    try {
      window.OneSignalDeferred = window.OneSignalDeferred || [];
      window.OneSignalDeferred.push(async function (OneSignal) {
        try {
          const optedIn = !!OneSignal?.User?.PushSubscription?.optedIn;
          setIsSubscribed(optedIn);
          try {
            window.localStorage.setItem(
              "yogapay_push_enabled",
              optedIn ? "1" : "0"
            );
          } catch {
            // ignore
          }
        } catch {
          // If OneSignal isn't ready or errors, fallback below
          try {
            const enabled =
              window.localStorage.getItem("yogapay_push_enabled") === "1";
            setIsSubscribed(enabled);
          } catch {
            // ignore
          }
        }
      });
      return;
    } catch {
      // ignore and fallback below
    }

    // 2) Fallback: localStorage
    try {
      const enabled = window.localStorage.getItem("yogapay_push_enabled") === "1";
      setIsSubscribed(enabled);
    } catch {
      // ignore
    }
  }, []);

  // Load home content
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const { data } = await api.get("/api/home");

        setSettings(data.settings || null);
        setAboutUs(data.about_us || null);

        setCarousel(data.carousel || []);

        // Support both "events" (array) and "event" (single)
        if (Array.isArray(data.events) && data.events.length) {
          setEvents(data.events);
        } else if (data.event) {
          setEvents([data.event]);
        } else {
          setEvents([]);
        }

        setErr("");
      } catch (e) {
        console.error(e);
        setErr(e?.response?.data?.message || "Failed to load home content");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  // Auto-rotate carousel every 4 seconds
  useEffect(() => {
    if (!carousel.length) return;
    const id = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % carousel.length);
    }, 4000);
    return () => clearInterval(id);
  }, [carousel]);

  // Auto-scroll events every 3.5 seconds (just changing index; UI shows one)
  useEffect(() => {
    if (!events.length) return;
    const id = setInterval(() => {
      setEventIndex((prev) => (prev + 1) % events.length);
    }, 3500);
    return () => clearInterval(id);
  }, [events]);

  const heroTitle = settings?.hero_heading || "Welcome to YogaPay";
  const heroSubtitle =
    settings?.hero_subtitle || "Online yoga event registrations made easy";
  const logoUrl = settings?.logo_url || null;

  const contactPhone = settings?.contact_phone || "";
  const contactEmail = settings?.contact_email || "";
  const contactAddress = settings?.contact_address || "";
  const hasContact = contactPhone || contactEmail || contactAddress;

  const currentSlide =
    carousel.length > 0
      ? carousel[Math.min(activeSlide, carousel.length - 1)]
      : null;

  const gotoPrev = () => {
    if (!carousel.length) return;
    setActiveSlide((idx) => (idx - 1 + carousel.length) % carousel.length);
  };

  const gotoNext = () => {
    if (!carousel.length) return;
    setActiveSlide((idx) => (idx + 1) % carousel.length);
  };

  // current event for ticker (single event at a time)
  const currentEvent =
    events.length > 0 ? events[Math.min(eventIndex, events.length - 1)] : null;

  // Feedback handlers (kept)
  const onFbChange = (e) => {
    const { name, value } = e.target;
    setFbForm((f) => ({ ...f, [name]: value }));
  };

  const onFbSubmit = async (e) => {
    e.preventDefault();
    setFbStatus("");
    try {
      setFbSubmitting(true);
      const payload = {
        name: fbForm.name || null,
        email: fbForm.email,
        mobile: fbForm.mobile || null,
        message: fbForm.message,
      };
      await api.post("/api/feedback", payload);
      setFbStatus("Thank you! We have received your message.");
      setFbForm({ name: "", email: "", mobile: "", message: "" });
    } catch (e) {
      console.error(e);
      setFbStatus(
        e?.response?.data?.message ||
          "Failed to send feedback. Please try again."
      );
    } finally {
      setFbSubmitting(false);
    }
  };

  // ----- 🔔 Bell toggle handler (OneSignal) -----
  const handleToggleNotifications = async () => {
    if (!pushSupported) {
      alert("Notifications are not supported in this browser.");
      return;
    }

    try {
      setBellLoading(true);

      // If browser has blocked notifications
      if (Notification.permission === "denied") {
        alert(
          "Notifications are blocked in your browser settings. Please enable them for this site."
        );
        return;
      }

      // keep your browser id generation (no harm, and doesn't change your flow)
      getBrowserId();

      // OneSignal must already be initialized in public/index.html
      window.OneSignalDeferred = window.OneSignalDeferred || [];

      await new Promise((resolve, reject) => {
        window.OneSignalDeferred.push(async function (OneSignal) {
          try {
            if (!OneSignal?.User?.PushSubscription) {
              alert("OneSignal is not ready yet. Please refresh and try again.");
              return resolve();
            }

            // Toggle subscribe state
            if (isSubscribed) {
              // Turn OFF
              await OneSignal.User.PushSubscription.optOut();
            } else {
              // Turn ON (will prompt permission if needed)
              await OneSignal.User.PushSubscription.optIn();
            }

            // Sync UI state from OneSignal
            const optedIn = !!OneSignal?.User?.PushSubscription?.optedIn;
            setIsSubscribed(optedIn);

            try {
              window.localStorage.setItem(
                "yogapay_push_enabled",
                optedIn ? "1" : "0"
              );
            } catch {}

            alert(
              optedIn
                ? "Notifications enabled for this browser."
                : "Notifications disabled for this browser."
            );

            resolve();
          } catch (err) {
            reject(err);
          }
        });
      });
    } catch (e) {
      console.error("Toggle notifications error", e);
      alert("Failed to update notification settings. Please try again.");
    } finally {
      setBellLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "linear-gradient(180deg, #0f172a 0%, #020617 60%, #020617 100%)",
        display: "flex",
        flexDirection: "column",
        color: "#0f172a",
      }}
    >
      {/* Top hero bar */}
      <div
        style={{
          padding: "18px 16px 0",
        }}
      >
        <div
          style={{
            maxWidth: 1100,
            margin: "0 auto",
          }}
        >
          <header
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
              color: "#e5e7eb",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {logoUrl && (
                <img
                  src={logoUrl}
                  alt="YogaPay logo"
                  style={{
                    width: 46,
                    height: 46,
                    borderRadius: 999,
                    objectFit: "cover",
                    background: "#f9fafb",
                    border: "2px solid rgba(148,163,184,0.5)",
                  }}
                />
              )}
              <div>
                <div
                  style={{
                    fontSize: 11,
                    textTransform: "uppercase",
                    letterSpacing: 1.5,
                    color: "#a5b4fc",
                  }}
                >
                  YogaPay
                </div>
                <h1
                  style={{
                    margin: 0,
                    fontSize: 22,
                    fontWeight: 700,
                    color: "#f9fafb",
                  }}
                >
                  {heroTitle}
                </h1>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {/* Bell icon (UI unchanged) */}
              <button
                type="button"
                onClick={handleToggleNotifications}
                disabled={!pushSupported || bellLoading}
                title={
                  !pushSupported
                    ? "Notifications not supported in this browser"
                    : isSubscribed
                    ? "Notifications enabled (click to disable)"
                    : "Enable browser notifications"
                }
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  border: "1px solid #e5e7eb",
                  background: isSubscribed ? "#dcfce7" : "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: !pushSupported || bellLoading ? "default" : "pointer",
                  fontSize: 18,
                }}
              >
                {isSubscribed ? "🔔" : "🔕"}
              </button>

              {/* Register button (unchanged) */}
              <Link
                to="/register"
                style={{
                  padding: "8px 16px",
                  borderRadius: 999,
                  background: "linear-gradient(135deg, #f97316, #fb923c)",
                  color: "#111827",
                  fontWeight: 700,
                  fontSize: 14,
                  textDecoration: "none",
                  boxShadow: "0 10px 25px rgba(15,23,42,0.4)",
                }}
              >
                Register Now
              </Link>
            </div>
          </header>

          <p
            style={{
              marginTop: 8,
              marginBottom: 16,
              fontSize: 13,
              maxWidth: 520,
              color: "#e5e7eb",
              opacity: 0.9,
            }}
          >
            {heroSubtitle}
          </p>
        </div>
      </div>

      {/* Main content card (unchanged) */}
      <main
        style={{
          padding: "0 16px 16px",
          flex: 1,
        }}
      >
        <div
          style={{
            maxWidth: 1100,
            margin: "0 auto",
          }}
        >
          <div
            style={{
              background: "#f9fafb",
              borderRadius: 24,
              padding: 16,
              boxShadow: "0 18px 40px rgba(15,23,42,0.55)",
              border: "1px solid #1e293b",
            }}
          >
            {loading && (
              <div style={{ fontSize: 13, marginBottom: 10 }}>
                Loading home content…
              </div>
            )}
            {err && !loading && (
              <div
                style={{
                  color: "#b91c1c",
                  marginBottom: 10,
                  fontSize: 13,
                }}
              >
                {err}
              </div>
            )}

            {/* Row 1: Carousel (full width) */}
            <section style={{ marginBottom: 16 }}>
              {currentSlide ? (
                <section
                  aria-label="Yoga images"
                  style={{
                    borderRadius: 18,
                    overflow: "hidden",
                    background: "#020617",
                    color: "#f9fafb",
                    position: "relative",
                    minHeight: 220,
                  }}
                >
                  <img
                    src={currentSlide.image_url}
                    alt={currentSlide.title || "Yoga slide"}
                    style={{
                      width: "100%",
                      maxHeight: 260,
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      padding: "10px 12px",
                      background:
                        "linear-gradient(to top, rgba(15,23,42,0.98), rgba(15,23,42,0.05))",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-end",
                        gap: 8,
                      }}
                    >
                      <div>
                        {currentSlide.title && (
                          <div
                            style={{
                              fontWeight: 600,
                              fontSize: 14,
                            }}
                          >
                            {currentSlide.title}
                          </div>
                        )}
                        {currentSlide.caption && (
                          <div
                            style={{
                              fontSize: 12,
                              opacity: 0.9,
                              maxWidth: 420,
                            }}
                          >
                            {currentSlide.caption}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Arrows */}
                  {carousel.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={gotoPrev}
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: 10,
                          transform: "translateY(-50%)",
                          borderRadius: 999,
                          border: "none",
                          width: 30,
                          height: 30,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "rgba(15,23,42,0.75)",
                          color: "#f9fafb",
                          cursor: "pointer",
                        }}
                      >
                        ‹
                      </button>
                      <button
                        type="button"
                        onClick={gotoNext}
                        style={{
                          position: "absolute",
                          top: "50%",
                          right: 10,
                          transform: "translateY(-50%)",
                          borderRadius: 999,
                          border: "none",
                          width: 30,
                          height: 30,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "rgba(15,23,42,0.75)",
                          color: "#f9fafb",
                          cursor: "pointer",
                        }}
                      >
                        ›
                      </button>
                    </>
                  )}

                  {/* Dots */}
                  {carousel.length > 1 && (
                    <div
                      style={{
                        position: "absolute",
                        bottom: 8,
                        right: 12,
                        display: "flex",
                        gap: 4,
                      }}
                    >
                      {carousel.map((_, idx) => (
                        <div
                          key={idx}
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background:
                              idx === activeSlide
                                ? "#22c55e"
                                : "rgba(148,163,184,0.7)",
                          }}
                        />
                      ))}
                    </div>
                  )}
                </section>
              ) : (
                <div
                  style={{
                    borderRadius: 18,
                    border: "1px dashed #cbd5e1",
                    background: "#f8fafc",
                    minHeight: 220,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    color: "#64748b",
                  }}
                >
                  No carousel images yet.
                </div>
              )}
            </section>

            {/* Row 2: Events (full width, below carousel) */}
            <section style={{ marginBottom: 16 }}>
              {events.length > 0 && currentEvent ? (
                <section
                  style={{
                    padding: 14,
                    borderRadius: 18,
                    background: "#eff6ff",
                    border: "1px solid #bfdbfe",
                    minHeight: 180,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      color: "#1d4ed8",
                      marginBottom: 6,
                      fontWeight: 700,
                      letterSpacing: 0.6,
                      textTransform: "uppercase",
                    }}
                  >
                    Upcoming Events
                  </div>

                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <h3
                      style={{
                        margin: "0 0 6px",
                        fontSize: 15,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={currentEvent.title}
                    >
                      {currentEvent.title}
                    </h3>

                    {currentEvent.event_date && (
                      <div
                        style={{
                          fontSize: 13,
                          marginBottom: 4,
                          wordBreak: "break-word",
                        }}
                      >
                        <strong>Date:</strong> {currentEvent.event_date}
                      </div>
                    )}

                    {currentEvent.location && (
                      <div
                        style={{
                          fontSize: 13,
                          marginBottom: 4,
                          wordBreak: "break-word",
                        }}
                      >
                        <strong>Location:</strong> {currentEvent.location}
                      </div>
                    )}

                    {currentEvent.description && (
                      <p
                        style={{
                          fontSize: 13,
                          marginTop: 4,
                          marginBottom: 0,
                          overflow: "hidden",
                        }}
                      >
                        {currentEvent.description}
                      </p>
                    )}
                  </div>

                  {events.length > 1 && (
                    <div
                      style={{
                        marginTop: 8,
                        fontSize: 11,
                        color: "#1d4ed8",
                        textAlign: "right",
                      }}
                    >
                      {eventIndex + 1} / {events.length}
                    </div>
                  )}
                </section>
              ) : (
                <div
                  style={{
                    borderRadius: 18,
                    border: "1px dashed #cbd5e1",
                    background: "#f8fafc",
                    minHeight: 180,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    color: "#64748b",
                  }}
                >
                  No upcoming events yet.
                </div>
              )}
            </section>

            {/* About Us (only active item) */}
            {aboutUs && (
              <section
                style={{
                  marginTop: 18,
                  borderRadius: 18,
                  border: "1px solid rgba(148,163,184,0.35)",
                  background: "rgba(255,255,255,0.96)",
                  padding: 18,
                  boxShadow: "0 12px 30px rgba(2, 6, 23, 0.08)",
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    color: "#1d4ed8",
                    marginBottom: 6,
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    textTransform: "uppercase",
                  }}
                ></div>

                <div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a" }}>
                  {aboutUs.title}
                </div>

                <div
                  style={{
                    marginTop: 10,
                    fontSize: 14,
                    lineHeight: 1.7,
                    color: "#334155",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {aboutUs.content}
                </div>
              </section>
            )}

            {/* Contact row (feedback commented) */}
            {hasContact && (
              <section
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr)",
                  gap: 16,
                }}
              >
                {/* Contact card */}
                <div
                  style={{
                    padding: 14,
                    borderRadius: 16,
                    background: "#ffffff",
                    border: "1px solid #e2e8f0",
                    fontSize: 13,
                  }}
                >
                  <h3
                    style={{
                      margin: "0 0 8px",
                      fontSize: 15,
                      fontWeight: 600,
                      color: "#0f172a",
                    }}
                  >
                    Contact
                  </h3>
                  {contactPhone && (
                    <div style={{ marginBottom: 4 }}>
                      <strong>Phone:</strong> {contactPhone}
                    </div>
                  )}
                  {contactEmail && (
                    <div style={{ marginBottom: 4 }}>
                      <strong>Email:</strong> {contactEmail}
                    </div>
                  )}
                  {contactAddress && (
                    <div style={{ marginBottom: 4 }}>
                      <strong>Address:</strong> {contactAddress}
                    </div>
                  )}
                </div>

                {/* Feedback block is still commented exactly as before */}
              </section>
            )}
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
